/**
 * TYPES
 */
import {IContact} from 'src/aggregates/customers/state.d';
import {IMessage} from 'src/aggregates/timelines/state.d';
import {ITimelineConversation} from 'src/aggregates/timelines/state.d';
import {ITimelineEvent} from 'src/aggregates/timelines/state.d';
import {ITimelineState} from 'src/aggregates/timelines/state.d';
import Store from 'src/store';


/**
 * CODE
 */

/**
 * I get timeline events by conversation id.
 *
 * :param conversation: conversation id
 *
 * :returns: timeline events or null
 */
function getByConversation (conversation: number): ITimelineEvent[] | null
{
    return getState().byConversation.get(conversation) ?? null;
}


/**
 * I get a conversation by id.
 *
 * :param id: conversation id
 *
 * :returns: conversation or null
 */
function getConversation (id: number): ITimelineConversation | null
{
    // get conversation
    const conversation = getState().timeline.find(
        (conversation: ITimelineConversation): boolean => conversation.id === id,
    );

    // return conversation if it was found, null otherwise
    return conversation ? conversation : null;
}


/*
 * I get last conversation.
 *
 * :param contacts: customer contacts
 *
 * :returns: last conversation or null
 */
function getLastConversation(contacts: IContact[]): ITimelineConversation | null
{
    // no conversations: return null
    const conversations = getState().timeline;
    if (conversations.length === 0)
    {
        return null;
    }

    // filter conversations by contacts
    const remotes = contacts.map(contact => contact.value);
    const filtered = conversations.filter(conversation => (
        remotes.includes(conversation.remote)
    ));

    // no conversations by contacts: return null
    if (filtered.length === 0)
    {
        return null;
    }

    // get last conversation
    let lastConversation = filtered[0];
    if (filtered.length > 1)
    {
        lastConversation = filtered.sort((a, b) => (
            a.lastInteraction > b.lastInteraction ? -1 : 1
        ))[0];
    }

    // return last conversation
    return lastConversation;
}


/*
 * I get last message.
 *
 * :param contacts: customer contacts
 *
 * :returns: last message or null
 */
function getLastMessage(contacts: IContact[]): IMessage | null
{
    // last conversation not found: return null
    const lastConversation = getLastConversation(contacts);
    if (lastConversation === null)
    {
        return null;
    }

    // get last message
    const messages: IMessage[] = lastConversation.messages ?? [];
    let lastMessage = messages[0] ?? null;

    // no messages: return null
    if (lastMessage === null)
    {
        return null;
    }

    // has several messages: get the last
    if (messages.length > 0)
    {
        lastMessage = messages.sort((a, b) => (
            a.timestamp > b.timestamp ? -1 : 1
        ))[0];
    }

    // return last message
    return lastMessage;
}


/**
 * I get id of conversation that will have its messages loaded.
 *
 * :returns: conversation or null
 */
function getNextConversation (
    conversation?: number | null,
): ITimelineConversation
{
    // get timeline
    const timeline = conversation !== undefined && conversation !== null
        ? (getByConversation(conversation) ?? [])
        : getState().timeline;

    // find conversation to load messages
    for (const conversation of timeline)
    {
        // conversation has not fetch messages yet: return conversation id
        if (conversation.messages === undefined)
        {
            return conversation;
        }
    }

    // all conversations of timeline have already fetch its messages:
    // return null
    return null;
}


/**
 * I get timeline aggregate state.
 *
 * :returns: timeline aggregate state
 */
function getState(): ITimelineState
{
    return Store.getState().timeline;
}


/**
 * I get all timeline loaded messages for a contact.
 *
 * :param state: timeline state
 *
 * :returns: array of messages
 */
function getTimelineMessages (state: ITimelineState): IMessage[]
{
    // initialize messages array
    let messages: IMessage[] = [];

    // for each conversation in timeline
    for (const conversation of state.timeline)
    {
        // conversation has messages: add messages to beginning of array
        if (conversation.messages != undefined)
        {
            messages = conversation.messages.concat(messages);
        }
    }

    // return messages
    return messages;
}


/**
 * EXPORTS
 */
export {
    getByConversation,
    getConversation,
    getLastConversation,
    getLastMessage,
    getNextConversation,
    getState,
    getTimelineMessages,
};
